<script lang="ts">
import { PropType, defineComponent, h, resolveComponent, type VNode } from 'vue';

import { formatPhone } from '@/utils/filters';
import { IBroker } from '@/bundles/Brokers/interfaces/IBroker';
import { getAccountRouteHandler } from '@/bundles/App/helpers';

const getBrokerTemplate = (broker: IBroker, activeAccountId: number) => {
  const RouterLink = resolveComponent('router-link');
  const template: VNode[] = [
    h(RouterLink,
      {
        target: '_blank',
        class: 'broker-name',
        to: getAccountRouteHandler({ name: 'contact', params: { id: broker._key }, activeAccountId })
      },
      { default: () => `${broker.name}` }
    )
  ];

  if (broker?.phone?.direct) {
    template.push(h('div', [
      h('strong', 'Direct: '),
      h('a', { href: `tel:${broker.phone.direct}` }, [
        h('span', formatPhone(broker.phone.direct)),
      ]),
    ]));
  }

  if (broker?.phone?.mobile) {
    template.push(h('div', [
      h('strong', 'Mobile: '),
      h('a', { href: `tel:${broker.phone.mobile}` }, [
        h('span', formatPhone(broker.phone.mobile)),
      ]),
    ]));
  }

  template.push(h('div', broker.email));

  return template;
}

export default defineComponent({
  name: 'AvailabilityBrokersInfo',

  props: {
    brokers: {
      type: Array as PropType<IBroker[]>,
      required: true
    },
    activeAccountId: {
      type: Number,
      required: true
    },
    withTooltipForAllBrokers: Boolean
  },

  render (): VNode {
    const { brokers, activeAccountId, withTooltipForAllBrokers } = this;
    const VRow = resolveComponent('v-row');
    const VCol = resolveComponent('v-col');

    const cols: VNode[] = [];

    if (!brokers.length) {
      return h(VRow, null, { default: () => cols });
    }

    if (!withTooltipForAllBrokers) {
      brokers.forEach(broker => {
        const template = getBrokerTemplate(broker, activeAccountId);

        cols.push(h(VCol, { cols: 12 }, { default: () => template }));
      });
    } else {
      const [firstBroker, ...restBrokers] = brokers;
      const template = getBrokerTemplate(firstBroker, activeAccountId);

      if (restBrokers.length) {
        const VMenu = resolveComponent('v-menu');
        const VBtn = resolveComponent('v-btn');
        const VIcon = resolveComponent('v-icon');
        const VCard = resolveComponent('v-card');

        template.push(h(VMenu, {
          openOnHover: true,
          location: 'top start',
          transition: 'scale-transition',
          closeOnContentClick: false,
        },
        {
          activator: ({ props }: any) => {
            return h(VBtn, {
              size: 'small',
              ...props,
            }, { default: () => [
              'More',
              h(VIcon, { size: 'small' }, { default: () => 'mdi-information-outline' }),
            ] });
          },
          default: () => [
            h(VCard, null, { default: () => [
              ...restBrokers.map(broker => {
                const template = getBrokerTemplate(broker, activeAccountId);
                return h(VCol, { cols: 12 }, { default: () => template });
              })
            ] })
          ],
        }));
      }

      cols.push(h(VCol, { cols: 12 }, { default: () => template }));
    }

    return h(VRow, null, { default: () => cols });
  }
})
</script>
